import { Link } from "react-router-dom";
import { Building2, FileCheck2, Handshake, Target } from "lucide-react";
import { Btn, Card, SectionTitle } from "@/components/Ui";

const BENEFITS = [
  { Icon: Target, t: "Only relevant enquiries", d: "Matching is driven by your registered categories, subcategories, brands and markets served — not by broad category blasts." },
  { Icon: FileCheck2, t: "Your data, normalized", d: "ADIT maps your range into the platform parameter set so buyers find your parts as technical equivalents." },
  { Icon: Handshake, t: "Structured quoting", d: "Quote technically and commercially in one structured form, including deviations, certificates and documents." },
  { Icon: Building2, t: "Verified company profile", d: "Trade licence, certifications and insurance documents are reviewed before verified status is granted." },
];

const REQUIRED = [
  "Trade licence / company registration",
  "Product catalogue or datasheet pack",
  "IECEx / ATEX certificates with reference numbers",
  "Technical parameters per part number (thread, armour, cable range, material, IP, temperature)",
  "Insurance documents where applicable",
  "Commercial contact for RFQ routing",
];

export default function SupplierJoin() {
  return (
    <div data-testid="supplier-join-page">
      <section className="border-b border-[#DFE6ED] bg-[#102A43]">
        <div className="mx-auto max-w-[1240px] px-5 py-16 lg:py-20">
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#D9A441]">
            For manufacturers, distributors, stockists and trading companies
          </p>
          <h1 className="mt-4 max-w-3xl font-display text-3xl font-black leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">
            Join as a supplier on ADIT Connect
          </h1>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-[#bcccdc] md:text-base">
            Get your range mapped into GCC specifications and receive structured enquiries that match
            what you actually supply.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/register?role=supplier">
              <Btn variant="gold" data-testid="join-register-btn">Create a supplier account</Btn>
            </Link>
            <Link to="/pricing">
              <Btn variant="ghost" data-testid="join-pricing-btn">View subscription plans</Btn>
            </Link>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1240px] px-5 py-14 lg:py-20">
        <SectionTitle overline="Why register" title="What the platform gives a supplier" />
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {BENEFITS.map((b) => (
            <Card key={b.t}>
              <span className="grid h-10 w-10 place-items-center rounded-[9px] bg-[#eef2f7] text-[#102A43]">
                <b.Icon className="h-5 w-5" />
              </span>
              <h3 className="mt-4 font-display text-base font-bold tracking-tight text-[#102A43]">{b.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#667788]">{b.d}</p>
            </Card>
          ))}
        </div>
      </section>

      <section className="border-t border-[#DFE6ED] bg-white">
        <div className="mx-auto grid max-w-[1240px] gap-10 px-5 py-14 lg:grid-cols-2 lg:py-20">
          <div>
            <SectionTitle overline="Verification" title="What we need to verify your company" />
            <ul className="mt-7 space-y-2.5">
              {REQUIRED.map((r) => (
                <li key={r} className="flex gap-2.5 text-sm text-[#334e68]">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#147D78]" />
                  {r}
                </li>
              ))}
            </ul>
            <p className="mt-6 max-w-xl text-sm leading-relaxed text-[#667788]">
              You can register immediately and upload documents from your supplier dashboard. Your
              profile stays in <strong>pending verification</strong> until the ADIT team completes the
              review.
            </p>
          </div>
          <Card>
            <h3 className="font-display text-lg font-bold tracking-tight text-[#102A43]">
              Seller responsibility
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-[#667788]">
              Every quotation submitted on ADIT Connect requires the supplier to confirm:
            </p>
            <p className="mt-4 rounded-[9px] border-l-2 border-[#D9A441] bg-[#F5F7FA] p-4 text-sm leading-relaxed text-[#334e68]">
              “I confirm that the information submitted in this quotation is accurate and that our
              company is responsible for the offered product, specifications, documentation, delivery
              commitment, warranty and applicable seller obligations.”
            </p>
            <Link to="/register?role=supplier" className="mt-6 inline-block">
              <Btn data-testid="join-register-btn-2">Register now</Btn>
            </Link>
          </Card>
        </div>
      </section>
    </div>
  );
}
